import { useCallback, useEffect, useRef, useState } from 'react'
import CrmIntegrationChatsList from '@pages/Admin/CRM/components/integrations/common/components/CrmIntegrationChatsList/CrmIntegrationChatsList'
import CrmIntegrationChat from '@pages/Admin/CRM/components/integrations/common/components/CrmIntegrationChat/CrmIntegrationChat'
import { useIntegrationChannelDeletedRealtime } from '@pages/Admin/CRM/components/integrations/common/hooks/useIntegrationChannelDeletedRealtime'
import { parseCrmChatProviderKey, resolveCrmChatProviderKey } from '@pages/Admin/CRM/components/integrations/common/lib/crmChatProviderKey'
import { isIntegrationCrmAccessible } from '@pages/Admin/CRM/utils/crmIntegrationAccess'
import type { IntegrationItem } from '@/api/integrations/integrations.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import {
  useIntegrationChatDock,
  useIntegrationChatDockNotify,
} from './integrationChatDockHooks'
import IntegrationChatDockIntegrationsPicker from './IntegrationChatDockIntegrationsPicker'
import IntegrationChatDockWazzupChannelsPicker from './IntegrationChatDockWazzupChannelsPicker'
import { usePlatformChatOpenDimensions } from './usePlatformChatOpenDimensions'
import { useIntegrationChatDockGlobalUnreadRefetch } from './useIntegrationChatDockGlobalUnreadRefetch'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import { useWazzupCrmAccounts } from './useWazzupCrmAccounts'
import classes from './IntegrationChatDock.module.css'

const IntegrationChatDockPanel = () => {
  const {
    leadId,
    conversationId,
    communityTabs,
    activeProvider,
    chatsListIntegrationId,
    chatsListExternalChannelId,
    expanded,
    chatScreen,
    providerUnreadCounts,
    providerMyUnreadCounts,
    unreadByIntegrationId,
    toggleExpanded,
    switchDockLead,
    openProviderChats,
    openWazzupAccountChannels,
    openWazzupChannelChats,
    backToIntegrationsPicker,
    backToWazzupAccounts,
    backToWazzupChannels,
    backToProviderChatsList,
    setConversationId: setDockConversationId,
    setChatsListIntegrationId,
  } = useIntegrationChatDock()
  const { notifyMarkReadSuccess } = useIntegrationChatDockNotify()
  const addAlert = useAddAlert()
  const scheduleGlobalUnreadRefetchRef =
    useIntegrationChatDockGlobalUnreadRefetch()
  const { width, height } = usePlatformChatOpenDimensions()
  const { isLoading, providers } = useCrmIntegrationChatAvailability()
  const shouldLoadWazzupAccounts =
    expanded &&
    leadId == null &&
    (chatScreen === 'integrations-picker' ||
      chatScreen === 'wazzup-accounts' ||
      chatScreen === 'wazzup-channels')
  const {
    accounts: wazzupAccounts,
    isLoading: wazzupAccountsLoading,
  } = useWazzupCrmAccounts(shouldLoadWazzupAccounts)
  const [wazzupAccountMeta, setWazzupAccountMeta] = useState<{
    name: string
    isAccessible: boolean
  } | null>(null)
  const [wazzupChannelsRefreshToken, setWazzupChannelsRefreshToken] =
    useState(0)
  const prevChatScreenRef = useRef(chatScreen)

  useEffect(() => {
    const enteredWazzupChannels =
      chatScreen === 'wazzup-channels' &&
      prevChatScreenRef.current !== 'wazzup-channels' &&
      chatsListIntegrationId != null
    // Remount picker уже делает первый GET; token только при возврате со списка чатов,
    // чтобы не гонять silent поверх loading и не залипать «Загрузка каналов…».
    if (
      enteredWazzupChannels &&
      prevChatScreenRef.current === 'chats-list'
    ) {
      setWazzupChannelsRefreshToken((token) => token + 1)
    }
    prevChatScreenRef.current = chatScreen
  }, [chatScreen, chatsListIntegrationId])

  const handleDeletedActiveWazzupChannel = useCallback(
    (payload: { channel_name?: string }) => {
      const channelName = String(payload.channel_name ?? '').trim()
      addAlert({
        title: 'Канал удалён',
        text: channelName
          ? `Канал «${channelName}» больше недоступен`
          : 'Канал Wazzup удалён и больше недоступен',
        type: 'warning',
      })
      backToWazzupChannels()
    },
    [addAlert, backToWazzupChannels],
  )

  const handleDockConversationChannelDeleted = useCallback(() => {
    if (chatsListExternalChannelId?.trim()) {
      backToWazzupChannels()
      return
    }
    backToProviderChatsList()
  }, [
    backToProviderChatsList,
    backToWazzupChannels,
    chatsListExternalChannelId,
  ])

  useIntegrationChannelDeletedRealtime({
    enabled:
      expanded &&
      activeProvider === 'wazzup' &&
      chatsListIntegrationId != null &&
      Boolean(chatsListExternalChannelId?.trim()) &&
      chatScreen === 'chats-list',
    integrationId: chatsListIntegrationId,
    externalChannelId: chatsListExternalChannelId,
    onDeleted: handleDeletedActiveWazzupChannel,
  })

  const handleMarkReadSuccess = useCallback(
    (id: number, readConversationId?: number) => {
      notifyMarkReadSuccess(id, readConversationId)
      scheduleGlobalUnreadRefetchRef.current()
    },
    [notifyMarkReadSuccess, scheduleGlobalUnreadRefetchRef],
  )

  const handleSummaryChange = useCallback(() => {
    scheduleGlobalUnreadRefetchRef.current()
  }, [scheduleGlobalUnreadRefetchRef])

  const handleSelectWazzupAccount = useCallback(
    (account: IntegrationItem) => {
      setWazzupAccountMeta({
        name: account.name?.trim() || `Аккаунт #${account.id}`,
        isAccessible: isIntegrationCrmAccessible(account),
      })
      openWazzupAccountChannels(account.id)
    },
    [openWazzupAccountChannels],
  )

  useEffect(() => {
    if (
      expanded &&
      leadId == null &&
      chatScreen === 'integrations-picker'
    ) {
      scheduleGlobalUnreadRefetchRef.current()
    }
  }, [chatScreen, expanded, leadId, scheduleGlobalUnreadRefetchRef])

  useEffect(() => {
    if (chatScreen === 'integrations-picker' || activeProvider !== 'wazzup') {
      setWazzupAccountMeta(null)
    }
  }, [activeProvider, chatScreen])

  // Restore account title when returning to channels from chats-list.
  useEffect(() => {
    if (
      chatScreen !== 'wazzup-channels' ||
      chatsListIntegrationId == null ||
      wazzupAccountMeta != null
    ) {
      return
    }
    const account = wazzupAccounts.find(
      (item) => item.id === chatsListIntegrationId,
    )
    if (!account) {
      return
    }
    setWazzupAccountMeta({
      name: account.name?.trim() || `Аккаунт #${account.id}`,
      isAccessible: isIntegrationCrmAccessible(account),
    })
  }, [
    chatScreen,
    chatsListIntegrationId,
    wazzupAccountMeta,
    wazzupAccounts,
  ])

  if (!expanded) {
    return null
  }

  const panelStyle = {
    width,
    height,
    maxWidth: width,
    maxHeight: height,
    minHeight: height,
  }

  if (
    leadId == null &&
    (chatScreen === 'integrations-picker' || chatScreen === 'wazzup-accounts')
  ) {
    return (
      <div className={classes.panel} style={panelStyle}>
        <div className={classes.panelBody}>
          <IntegrationChatDockIntegrationsPicker
            providers={providers}
            providerUnreadCounts={providerUnreadCounts}
            providerMyUnreadCounts={providerMyUnreadCounts}
            wazzupAccounts={wazzupAccounts}
            wazzupAccountsLoading={wazzupAccountsLoading}
            unreadByIntegrationId={unreadByIntegrationId}
            isLoading={isLoading}
            onCollapse={toggleExpanded}
            onSelectProvider={openProviderChats}
            onSelectWazzupAccount={handleSelectWazzupAccount}
          />
        </div>
      </div>
    )
  }

  if (
    leadId == null &&
    chatScreen === 'wazzup-channels' &&
    chatsListIntegrationId != null
  ) {
    return (
      <div className={classes.panel} style={panelStyle}>
        <div className={classes.panelBody}>
          <IntegrationChatDockWazzupChannelsPicker
            integrationId={chatsListIntegrationId}
            accountName={wazzupAccountMeta?.name}
            accountAccessible={wazzupAccountMeta?.isAccessible ?? true}
            refreshToken={wazzupChannelsRefreshToken}
            onCollapse={toggleExpanded}
            onBack={backToWazzupAccounts}
            onSelectChannel={(channel) => {
              const externalChannelId = String(
                channel.external_channel_id ?? '',
              ).trim()
              if (!externalChannelId) {
                return
              }
              openWazzupChannelChats(chatsListIntegrationId, externalChannelId)
            }}
          />
        </div>
      </div>
    )
  }

  if (leadId == null && chatScreen === 'chats-list' && activeProvider != null) {
    const chatsListBack =
      activeProvider === 'wazzup'
        ? chatsListIntegrationId != null
          ? backToWazzupChannels
          : backToIntegrationsPicker
        : backToIntegrationsPicker

    return (
      <div className={classes.panel} style={panelStyle}>
        <div className={classes.panelBody}>
          <CrmIntegrationChatsList
            provider={activeProvider}
            variant="dock"
            integrationId={chatsListIntegrationId ?? undefined}
            externalChannelId={
              activeProvider === 'wazzup' ? chatsListExternalChannelId : null
            }
            onIntegrationIdChange={setChatsListIntegrationId}
            onCollapse={toggleExpanded}
            onBack={chatsListBack}
            onSelectChat={(selection) => {
              if (selection.leadId == null) {
                return
              }
              const provider = parseCrmChatProviderKey(selection.provider)
              switchDockLead(selection.leadId, {
                conversationId: selection.conversationId,
                ...(provider ? { provider } : {}),
              })
            }}
          />
        </div>
      </div>
    )
  }

  if (leadId == null) {
    return (
      <div className={classes.panel} style={panelStyle}>
        <div className={classes.panelBody}>
          <IntegrationChatDockIntegrationsPicker
            providers={providers}
            providerUnreadCounts={providerUnreadCounts}
            providerMyUnreadCounts={providerMyUnreadCounts}
            wazzupAccounts={wazzupAccounts}
            wazzupAccountsLoading={wazzupAccountsLoading}
            unreadByIntegrationId={unreadByIntegrationId}
            isLoading={isLoading}
            onCollapse={toggleExpanded}
            onSelectProvider={openProviderChats}
            onSelectWazzupAccount={handleSelectWazzupAccount}
          />
        </div>
      </div>
    )
  }

  const conversationProvider = resolveCrmChatProviderKey(activeProvider)

  return (
    <div className={classes.panel} style={panelStyle}>
      <div className={classes.panelBody}>
        <CrmIntegrationChat
          provider={conversationProvider}
          leadId={leadId}
          conversationId={conversationId}
          communityTabs={communityTabs}
          variant="dock"
          enabled
          onBackFromConversation={backToProviderChatsList}
          onChannelDeleted={handleDockConversationChannelDeleted}
          onCollapse={toggleExpanded}
          onMarkReadSuccess={handleMarkReadSuccess}
          onSummaryChange={handleSummaryChange}
          onLeadSelect={(id, options) => {
            switchDockLead(id, {
              ...options,
              provider: conversationProvider,
            })
          }}
          onConversationIdChange={setDockConversationId}
        />
      </div>
    </div>
  )
}

export default IntegrationChatDockPanel
